import React, { useState } from 'react';
import styled from 'styled-components';

interface SharePredictionProps {
  prediction: {
    id: number;
    influencer?: {
      name?: string;
      x_handle?: string;
    };
    expert_name?: string;
    price_prediction?: string;
    price_min?: number;
    price_max?: number;
    prediction_type?: string;
    success_status?: string;
    target_time?: string;
    end_date?: string;
    quote?: string;
  };
  variant?: 'icon' | 'button' | 'full';
  subtle?: boolean; // More subtle styling for mobile
  onShare?: () => void;
}

const ShareContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

const ShareButton = styled.button<{ $variant: string; $subtle?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: ${props => props.$subtle ? '6px 10px' : props.$variant === 'icon' ? '8px' : '8px 14px'};
  background: ${props => props.$subtle 
    ? 'transparent' 
    : 'linear-gradient(135deg, rgba(29, 161, 242, 0.15) 0%, rgba(29, 161, 242, 0.05) 100%)'};
  border: 1px solid ${props => props.$subtle ? 'rgba(128, 128, 128, 0.3)' : 'rgba(29, 161, 242, 0.3)'};
  border-radius: 20px;
  color: ${props => props.$subtle ? 'rgba(255, 255, 255, 0.5)' : '#1DA1F2'};
  font-size: ${props => props.$subtle ? '12px' : '13px'};
  font-weight: ${props => props.$subtle ? '500' : '600'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$subtle ? 'rgba(128, 128, 128, 0.1)' : 'rgba(29, 161, 242, 0.25)'};
    border-color: ${props => props.$subtle ? 'rgba(128, 128, 128, 0.5)' : 'rgba(29, 161, 242, 0.5)'};
    color: ${props => props.$subtle ? 'rgba(255, 255, 255, 0.8)' : '#1DA1F2'};
    transform: scale(1.02);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  svg {
    width: ${props => props.$subtle ? '14px' : '16px'};
    height: ${props => props.$subtle ? '14px' : '16px'};
  }
`;

const ShareDropdown = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: rgba(30, 30, 45, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 8px;
  min-width: 200px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transform: ${props => props.$visible ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
`;

const ShareOption = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  color: #ffffff;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: none;
  border: none;
  color: #ffffff;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 6px 0;
`;

const CopiedToast = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%) ${props => props.$visible ? 'translateY(0)' : 'translateY(20px)'};
  background: rgba(16, 185, 129, 0.95);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  z-index: 10000;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

// Share icon
const ShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
  </svg>
);

// X/Twitter icon
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Copy icon
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
  </svg>
);

// Nostr icon
const NostrIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
  </svg>
);

const SharePrediction: React.FC<SharePredictionProps> = ({ 
  prediction, 
  variant = 'icon',
  subtle = false,
  onShare 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Build the shareable URL - use /btc route for proper deep linking
  const baseUrl = 'https://proofofprice.com';
  const predictionUrl = `${baseUrl}/btc?p=${prediction.id}`;
  
  // Get prediction details
  const influencerName = prediction.influencer?.name || prediction.expert_name || 'Expert';
  const influencerHandle = prediction.influencer?.x_handle ? `@${prediction.influencer.x_handle.replace('@', '')}` : '';
  const price = prediction.price_prediction || 
    (prediction.price_min ? `$${prediction.price_min.toLocaleString()}` : '');
  const status = prediction.success_status;
  const targetDate = prediction.target_time || prediction.end_date;
  const formattedDate = targetDate 
    ? new Date(targetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  
  // Build tweet text based on status
  const buildTweetText = () => {
    let emoji = '🔮';
    let statusText = '';
    
    if (status === 'success') {
      emoji = '✅';
      statusText = 'CORRECT!';
    } else if (status === 'miss') {
      emoji = '❌';
      statusText = 'MISSED';
    } else {
      emoji = '🎯';
      statusText = 'TRACKING';
    }
    
    const parts = [
      `${emoji} ${influencerName}${influencerHandle ? ` (${influencerHandle})` : ''}'s Bitcoin prediction: ${price}`,
    ];
    
    if (formattedDate) {
      parts.push(`📅 Target: ${formattedDate}`);
    }
    
    if (status && status !== 'pending') {
      parts.push(`📊 Result: ${statusText}`);
    }
    
    parts.push('');
    parts.push('Track predictions at @proofofprice 📈');
    parts.push(predictionUrl);
    
    return parts.join('\n');
  };
  
  const tweetText = buildTweetText();
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
  
  // Copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(predictionUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      setIsOpen(false);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = predictionUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      setIsOpen(false);
    }
  };
  
  // Native share (mobile)
  const handleNativeShare = async () => {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: `${influencerName}'s Bitcoin Prediction`,
          text: tweetText,
          url: predictionUrl,
        });
        onShare?.();
      } catch (err) {
        // User cancelled or error - just close dropdown
        setIsOpen(false);
      }
    } else {
      // Fallback to copy
      handleCopy();
    }
  };
  
  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isOpen && !(e.target as Element).closest('.share-container')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);
  
  return (
    <>
      <ShareContainer className="share-container">
        <ShareButton 
          $variant={variant}
          $subtle={subtle}
          onClick={(e) => {
            e.stopPropagation();
            // On mobile, try native share first
            if (typeof navigator.share === 'function' && variant === 'icon') {
              handleNativeShare();
            } else {
              setIsOpen(!isOpen);
            }
          }}
          title="Share prediction"
        >
          <ShareIcon />
          {variant !== 'icon' && 'Share'}
        </ShareButton>
        
        <ShareDropdown $visible={isOpen}>
          <ShareOption 
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setIsOpen(false);
              onShare?.();
            }}
          >
            <XIcon />
            Share on X
          </ShareOption>
          
          <CopyButton onClick={handleCopy}>
            <CopyIcon />
            Copy link
          </CopyButton>
          
          <Divider />
          
          <CopyButton onClick={handleNativeShare}>
            <ShareIcon />
            More options...
          </CopyButton>
        </ShareDropdown>
      </ShareContainer>
      
      <CopiedToast $visible={copied}>
        ✓ Link copied to clipboard!
      </CopiedToast>
    </>
  );
};

export default SharePrediction;
